<template>
  <el-dialog
    title="上传物流面单"
    :visible.sync="dialogVisible"
    width="820px"
    custom-class="upload-logistics-waybill-dialog"
    :close-on-click-modal="false"
    append-to-body
    @opened="onDialogOpened"
    @closed="onDialogClosed"
  >
    <div v-if="orderSummary.orderNo" class="order-summary">
      <span>采购单号：{{ orderSummary.orderNo }}</span>
      <span>采购日期：{{ orderSummary.date || "-" }}</span>
      <span>店铺：{{ orderSummary.shopName || "-" }}</span>
      <span>供应商：{{ orderSummary.supplierName || "-" }}</span>
    </div>

    <el-form ref="form" :model="form" :rules="rules" label-width="120px" size="small">
      <el-form-item label="实付金额">
        <el-input v-model="form.actualPaidAmount" disabled placeholder="自动带入"></el-input>
      </el-form-item>
      <el-form-item label="产品明细" required>
        <logistics-product-lines-editor ref="productLinesEditor" v-model="form.productLines" :product-list="productList" />
      </el-form-item>
      <el-form-item label="物流面单">
        <logistics-waybill-upload-panel
          ref="waybillPanel"
          :photos.sync="form.supplierWaybillPhotos"
          :active="pasteActive"
          :upload-pending="proofUploadPending"
          :barcode-recognizing="barcodeRecognizing"
          @focus-panel="pasteActive = true"
          @upload-start="markProofUploadStart"
          @upload-end="markProofUploadEnd"
          @barcode-start="barcodeRecognizing = true"
          @barcode-end="barcodeRecognizing = false"
          @barcode-result="applyBarcodeRecognition"
        />
      </el-form-item>
      <el-form-item label="物流单号" prop="logisticsNos">
        <el-select
          v-model="form.logisticsNos"
          multiple
          filterable
          allow-create
          default-first-option
          clearable
          placeholder="输入物流单号后回车添加"
          style="width: 100%"
        >
          <el-option v-for="no in form.logisticsNos" :key="no" :label="no" :value="no"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="物流箱子数" prop="boxCount">
        <el-input-number
          v-model="form.boxCount"
          class="box-count-input"
          :min="0"
          :precision="0"
          :controls="false"
          placeholder="请输入物流箱子数"
          style="width: 100%"
        ></el-input-number>
      </el-form-item>
      <el-form-item label="物流公司" prop="logisticsCompany">
        <el-select v-model="form.logisticsCompany" filterable clearable placeholder="请选择物流公司" style="width: 100%">
          <el-option v-for="name in logisticsCompanies" :key="name" :label="name" :value="name"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="备注">
        <el-input v-model="form.remark" type="textarea" :rows="2" maxlength="500" show-word-limit placeholder="选填"></el-input>
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" size="small" :loading="submitting" @click="submit">确定并新增物流</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { logisticsModules } from "@r";
import { activeProducts, allActiveProducts } from "../../utils/constant.js";
import LogisticsProductLinesEditor from "../purchase/LogisticsProductLinesEditor.vue";
import LogisticsWaybillUploadPanel from "../purchase/LogisticsWaybillUploadPanel.vue";
import {
  LOGISTICS_COMPANIES,
  createEmptyLogisticsForm,
  normalizeProductLines,
  normalizeStringList,
  serializeProductLinesForSubmit,
} from "../../utils/logisticsFormHelpers";

export default {
  name: "UploadLogisticsWaybillDialog",
  components: {
    LogisticsProductLinesEditor,
    LogisticsWaybillUploadPanel,
  },
  data() {
    return {
      dialogVisible: false,
      submitting: false,
      pasteActive: true,
      proofUploadPending: 0,
      barcodeRecognizing: false,
      orderSummary: {},
      productList: localStorage.getItem("shopType") == 6 ? allActiveProducts : activeProducts,
      logisticsCompanies: LOGISTICS_COMPANIES.slice(),
      form: createEmptyLogisticsForm(),
      rules: {
        logisticsCompany: [{ required: true, message: "请选择物流公司", trigger: "change" }],
      },
    };
  },
  methods: {
    open(orderRow, productLines) {
      if (!orderRow) return;
      const orderId = String(orderRow.orderId || orderRow._id || "");
      const lines = normalizeProductLines(productLines, this.productList);
      this.orderSummary = {
        orderNo: orderRow.orderNo || "",
        date: orderRow.date || "",
        shopName: orderRow.shopName || "",
        supplierName: orderRow.supplierName || orderRow.supplierShortName || "",
      };
      this.form = {
        ...createEmptyLogisticsForm(),
        orderNo: orderRow.orderNo || "",
        date: orderRow.date || "",
        shopName: orderRow.shopName || "",
        supplierId: orderRow.supplierId || "",
        supplierName: orderRow.supplierName || orderRow.supplierShortName || "",
        actualPaidAmount:
          orderRow.actualPaidAmount === "" || orderRow.actualPaidAmount == null
            ? ""
            : String(orderRow.actualPaidAmount),
        purchaseOrderId: orderId,
        source: "purchaseOrder",
        productLines: lines.length ? lines : createEmptyLogisticsForm().productLines,
      };
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate();
      });
    },
    onDialogOpened() {
      this.pasteActive = true;
      document.addEventListener("paste", this.handlePaste);
    },
    onDialogClosed() {
      document.removeEventListener("paste", this.handlePaste);
      this.form = createEmptyLogisticsForm();
      this.orderSummary = {};
      this.proofUploadPending = 0;
      this.barcodeRecognizing = false;
      this.submitting = false;
    },
    markProofUploadStart() {
      this.proofUploadPending += 1;
    },
    markProofUploadEnd() {
      this.proofUploadPending = Math.max(0, this.proofUploadPending - 1);
    },
    collectClipboardImageFiles(event) {
      const items = event && event.clipboardData && event.clipboardData.items;
      if (!items || !items.length) return [];
      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type && item.type.indexOf("image/") === 0) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      return files;
    },
    handlePaste(event) {
      if (!this.dialogVisible) return;
      const files = this.collectClipboardImageFiles(event);
      if (!files.length) return;
      event.preventDefault();
      files.forEach(file => {
        if (this.$refs.waybillPanel) {
          this.$refs.waybillPanel.uploadPastedFile(file);
        }
      });
    },
    buildBarcodeRecognitionMessage(logisticsNos, logisticsCompanies) {
      const lines = [`识别到 ${logisticsNos.length} 个物流单号：`].concat(logisticsNos);
      const willFillCompany = logisticsCompanies.length === 1 && !this.form.logisticsCompany;
      if (willFillCompany) {
        lines.push("", `物流公司：${logisticsCompanies[0]}（确认后将自动填入）`);
      } else if (logisticsCompanies.length > 1) {
        lines.push("", "检测到多个物流公司，确认单号后请手动选择物流公司。");
      } else if (this.form.logisticsCompany) {
        lines.push("", `当前物流公司：${this.form.logisticsCompany}（不会修改）`);
      } else {
        lines.push("", "未能匹配物流公司，确认单号后请手动选择。");
      }
      return lines.join("\n");
    },
    applyBarcodeRecognition(result) {
      const logisticsNos = normalizeStringList(result && result.logisticsNos);
      const logisticsCompanies = normalizeStringList(result && result.logisticsCompanies);
      if (!logisticsNos.length) {
        return this.$alert(
          "未能从面单照片中识别到物流单号，请检查图片是否清晰、条形码是否完整，或手动填写物流单号。",
          "面单识别结果",
          { confirmButtonText: "知道了", type: "warning", customClass: "barcode-recognition-confirm" }
        ).catch(() => {});
      }
      const willFillCompany = logisticsCompanies.length === 1 && !this.form.logisticsCompany;
      return this.$confirm(this.buildBarcodeRecognitionMessage(logisticsNos, logisticsCompanies), "面单识别结果", {
        confirmButtonText: "确认填入",
        cancelButtonText: "取消",
        type: "info",
        customClass: "barcode-recognition-confirm",
      })
        .then(() => {
          this.form.logisticsNos = normalizeStringList([...this.form.logisticsNos, ...logisticsNos]);
          if (willFillCompany) {
            this.form.logisticsCompany = logisticsCompanies[0];
          }
          this.$message.success(`已填入 ${logisticsNos.length} 个物流单号`);
        })
        .catch(() => {});
    },
    buildSubmitPayload() {
      const logisticsNos = normalizeStringList(this.form.logisticsNos);
      return {
        orderNo: (this.form.orderNo || "").trim(),
        date: this.form.date || "",
        shopName: this.form.shopName || "",
        supplierId: this.form.supplierId,
        supplierName: this.form.supplierName || "",
        actualPaidAmount: this.form.actualPaidAmount,
        purchaseOrderId: this.form.purchaseOrderId,
        source: this.form.source,
        productLines: serializeProductLinesForSubmit(this.form.productLines),
        logisticsCompany: this.form.logisticsCompany || "",
        logisticsNos: JSON.stringify(logisticsNos),
        boxCount: this.form.boxCount == null || this.form.boxCount === "" ? "" : this.form.boxCount,
        waybillNos: JSON.stringify([]),
        supplierWaybillPhotos: JSON.stringify(this.form.supplierWaybillPhotos || []),
        receiveLogisticsImages: JSON.stringify([]),
        remark: (this.form.remark || "").trim(),
      };
    },
    async checkDuplicateLogisticsHint(logisticsNos) {
      const hints = [];
      for (let i = 0; i < logisticsNos.length; i++) {
        const no = logisticsNos[i];
        const res = await logisticsModules.findLogisticsByNo({ logisticsNo: no });
        const count = res && res.mdata && res.mdata.matchCount != null ? res.mdata.matchCount : (res.ldata || []).length;
        if (count > 0) {
          hints.push(`物流单号 ${no} 已有 ${count} 条记录`);
        }
      }
      if (!hints.length) return true;
      try {
        await this.$confirm(`${hints.join("\n")}\n\n是否仍继续新建？`, "重复物流单号提示", {
          confirmButtonText: "继续新建",
          cancelButtonText: "取消",
          type: "info",
        });
        return true;
      } catch (e) {
        return false;
      }
    },
    async submit() {
      const lineError =
        (this.$refs.productLinesEditor && this.$refs.productLinesEditor.validate()) ||
        "";
      if (lineError) {
        this.$message.warning(lineError);
        return;
      }
      const logisticsNos = normalizeStringList(this.form.logisticsNos);
      if (!logisticsNos.length) {
        this.$message.warning("请填写或识别物流单号");
        return;
      }
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        const canContinue = await this.checkDuplicateLogisticsHint(logisticsNos);
        if (!canContinue) return;
        this.submitting = true;
        logisticsModules
          .addLogistics(this.buildSubmitPayload())
          .then(res => {
            if (!res || res.result !== 1) {
              this.$message.error((res && res.message) || "新增物流失败");
              return;
            }
            this.$message.success("物流记录已新增");
            this.dialogVisible = false;
            this.$emit("success");
          })
          .catch(err => {
            this.$message.error((err && err.message) || "新增物流失败");
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.order-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  color: #606266;
  font-size: 13px;
}
</style>

<style lang="scss">
.upload-logistics-waybill-dialog {
  .box-count-input .el-input__inner {
    text-align: left;
  }
}
</style>
